import palette from "assets/theme/base/palette";
import DataTable from "examples/Tables/DataTable";
import React, { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Button from "@mui/material/Button";
import MDInput from "components/MDInput";
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import { call } from "services/axiosService/core";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import MDButton from "components/MDButton";
import * as Sentry from "@sentry/browser";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const DeleteReferralButton = ({ id, DeleteReferralHandler }) => {
  return (
    <Tooltip fontSize="large" title={<h2 style={{}}>Delete Referral</h2>}>
      <MDTypography
        variant="body1"
        color="secondary"
        lineHeight={1}
        sx={{ cursor: "pointer", mx: 1 }}
      >
        <Icon
          fontSize="medium"
          color="error"
          onClick={() => {
            DeleteReferralHandler(id);
          }}
        >
          delete
        </Icon>
      </MDTypography>
    </Tooltip>
  );
};

export function ReferralsTable(props) {
  const [selectedId, setSelectedId] = useState("");
  const [selectedReferralDetails, setSelectedReferralDetails] = useState("");
  const [openViewDialog, setOpenViewDialog] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteReferralId, setDeleteReferralId] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const CloseViewDialogHandler = () => {
    setOpenViewDialog(false);
    setSelectedId("");
  };

  const DeleteReferralHandler = (id) => {
    setDeleteReferralId(id);
    setOpenDeleteDialog(true);
  };

  const CloseDeleteDialogHandler = () => {
    setOpenDeleteDialog(false);
    setSelectedId("");
  };

  useEffect(() => {
    if (!openDeleteDialog) {
      if (selectedId) {
        const referral = props.referrals.filter((row) => {
          return row.id == selectedId;
        });
        setSelectedReferralDetails(referral[0]);
        setOpenViewDialog(true);
      }
    }
  }, [selectedId]);

  const tableData = {
    columns: [
      { Header: "#", accessor: "num", width: "5%" },
      { Header: "Name", accessor: "name", width: "20%" },
      { Header: "RenewalCode", accessor: "renewal_code", width: "20%" },
      { Header: "TargetPrice", accessor: "target_price", width: "10%" },
      { Header: "RemaingUsageCount", accessor: "remaining_usage_count", width: "10%" },
      { Header: "Actions", accessor: "actions", width: "20%" },
    ],

    rows: props.referrals,
  };

  tableData.rows = tableData.rows.map((row, index) => ({
    ...row,
    renewal_code: row.renewal_code ? "true" : "false",
    target_price: row.renewal_code ? row.target_price[0] : row.target_price,
    num: index + 1,
    actions: (
      <div style={{ display: "flex" }}>
        <DeleteReferralButton id={row.id} DeleteReferralHandler={DeleteReferralHandler} />
      </div>
    ),
  }));

  return (
    <>
      <DataTable
        table={tableData}
        setSelectedId={setSelectedId}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        canSearch={true}
      />
      {openViewDialog && (
        <ViewReferralDataDialog
          openViewDialog={openViewDialog}
          CloseViewDialogHandler={CloseViewDialogHandler}
          selectedReferralDetails={selectedReferralDetails}
          loadReferrals={props.loadReferrals}
          setOpenSnackbar={setOpenSnackbar}
          setAlertMessage={setAlertMessage}
        />
      )}
      {openDeleteDialog && (
        <DeleteReferralDialog
          openDeleteDialog={openDeleteDialog}
          CloseDeleteDialogHandler={CloseDeleteDialogHandler}
          deleteReferralId={deleteReferralId}
          loadReferrals={props.loadReferrals}
          setOpenSnackbar={setOpenSnackbar}
          setAlertMessage={setAlertMessage}
        />
      )}
      <Snackbar
        autoHideDuration={2000}
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

const USES_PAGE_SIZE = 25;

const formatUsedAt = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

/**
 * Paginated redemption history for one referral code.
 *
 * This replaces dumping the code's whole `uses` array into the JSON textarea.
 * That textarea was an active corruption source - it PUT a stale snapshot of
 * the history back on every save - and on the largest code it moved ~89KB of
 * redemptions into the browser just to render an edit form. History is now
 * read through its own endpoint and is not editable from here at all.
 */
const ReferralUsesTable = ({ referralId }) => {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const loadPage = useCallback(
    (nextCursor) => {
      if (!referralId) return;
      setLoading(true);
      const query = `limit=${USES_PAGE_SIZE}${
        nextCursor ? `&cursor=${encodeURIComponent(nextCursor)}` : ""
      }`;
      call(`referrals/${referralId}/uses?${query}`, "GET", {})
        .then((res) => {
          if (res.status !== 200) {
            setFailed(true);
            return;
          }
          const page = res.data || {};
          setRows((current) => (nextCursor ? current.concat(page.items || []) : page.items || []));
          setCursor(page.next_cursor || null);
          setTotal(page.total || 0);
          setFailed(false);
        })
        .catch((err) => {
          Sentry.captureException(err);
          setFailed(true);
        })
        .finally(() => setLoading(false));
    },
    [referralId]
  );

  useEffect(() => {
    setRows([]);
    setCursor(null);
    setTotal(0);
    loadPage(null);
  }, [loadPage]);

  return (
    <MDBox p={1}>
      <MDTypography variant="h6" lineHeight={2}>
        Usage history {total > 0 ? `(${rows.length} of ${total})` : ""}
      </MDTypography>
      {failed && (
        <MDTypography variant="button" color="error">
          Could not load usage history.
        </MDTypography>
      )}
      {!failed && !loading && rows.length === 0 && (
        <MDTypography variant="button" color="text">
          This code has not been redeemed yet.
        </MDTypography>
      )}
      {rows.length > 0 && (
        <Table size="small">
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell align="right">Uses</TableCell>
              <TableCell>First used</TableCell>
              <TableCell>Last used</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.email}>
                <TableCell>
                  <MDTypography variant="button">{row.email}</MDTypography>
                </TableCell>
                <TableCell align="right">
                  <MDTypography variant="button">{row.use_count}</MDTypography>
                </TableCell>
                <TableCell>
                  <MDTypography variant="button">{formatUsedAt(row.first_used_at)}</MDTypography>
                </TableCell>
                <TableCell>
                  <MDTypography variant="button">{formatUsedAt(row.last_used_at)}</MDTypography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <MDBox mt={1} display="flex" alignItems="center">
        {loading && <CircularProgress size={18} />}
        {!loading && cursor && (
          <MDButton size="small" color="info" onClick={() => loadPage(cursor)}>
            Load more
          </MDButton>
        )}
      </MDBox>
    </MDBox>
  );
};

const ViewReferralDataDialog = ({
  openViewDialog,
  CloseViewDialogHandler,
  selectedReferralDetails,
  loadReferrals,
  setOpenSnackbar,
  setAlertMessage,
}) => {
  // The redemption history never enters the editor. It is shown read-only by
  // ReferralUsesTable below and is not writable through this PUT at all (the
  // API drops `uses` from this payload too, so a hand-edited body cannot
  // reintroduce it).
  const { uses, ...editableDetails } = selectedReferralDetails || {};
  const [details, setDetails] = useState(JSON.stringify(editableDetails, null, 3));
  const [validJson, setValidJson] = useState(true);

  const updateReferralHandler = () => {
    const data = JSON.parse(details);
    // The textarea still holds a snapshot taken when the dialog opened, so
    // only send the counter when the admin actually changed it - otherwise a
    // save reverts every redemption that landed while the dialog was open.
    delete data.uses;
    if (data.remaining_usage_count === selectedReferralDetails.remaining_usage_count) {
      delete data.remaining_usage_count;
    }
    call(`referrals/${data.id}`, "PUT", { body: data })
      .then((res) => {
        if (res.status == 200) {
          CloseViewDialogHandler();
          loadReferrals();
          setOpenSnackbar(true);
          setAlertMessage("Updated successfully");
        } else if (res.status == 440) {
          CloseViewDialogHandler();
        } else {
          setOpenSnackbar(true);
          setAlertMessage(res.data.message);
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        CloseViewDialogHandler();
      });
  };

  return (
    <Dialog open={openViewDialog} onClose={CloseViewDialogHandler} fullWidth={true} maxWidth={"md"}>
      <DialogTitle> Referral Details </DialogTitle>
      <DialogContent>
        <MDBox p={1} lineHeight={1}>
          <MDInput
            value={details}
            error={!validJson}
            multiline
            name="details"
            type="text"
            fullWidth
            required
            onChange={(e) => {
              setDetails(e.target.value);
              try {
                JSON.parse(e.target.value);
                setValidJson(true);
              } catch (error) {
                setValidJson(false);
              }
            }}
          />
        </MDBox>
        {!validJson && (
          <MDTypography
            textAlign="center"
            variant="h6"
            color="error"
            lineHeight={1}
            sx={{ cursor: "pointer", mx: 1 }}
          >
            Data is not a valid Json
          </MDTypography>
        )}
        <ReferralUsesTable referralId={selectedReferralDetails?.id} />
      </DialogContent>

      <DialogActions>
        {/* <Button  style={{ color: "white", background: palette.brand[500] }}>
            Delete
          </Button> */}
        <Button
          disabled={!validJson}
          onClick={updateReferralHandler}
          style={{ color: "white", background: palette.brand[500] }}
        >
          Save
        </Button>
        <Button
          onClick={CloseViewDialogHandler}
          style={{ color: "white", background: palette.pink[400] }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteReferralDialog = ({
  openDeleteDialog,
  CloseDeleteDialogHandler,
  deleteReferralId,
  loadReferrals,
  setOpenSnackbar,
  setAlertMessage,
}) => {
  const deleteReferral = () => {
    call(`referrals/${deleteReferralId}`, "DELETE", {})
      .then((res) => {
        if (res.status == 200) {
          CloseDeleteDialogHandler();
          loadReferrals();
          setOpenSnackbar(true);
          setAlertMessage("Deleted successfully");
        } else if (res.status == 440) {
          CloseDeleteDialogHandler();
        } else {
          setOpenSnackbar(true);
          setAlertMessage("Error while deleting the Referral");
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        CloseDeleteDialogHandler();
      });
  };

  return (
    <Dialog open={openDeleteDialog} onClose={CloseDeleteDialogHandler} maxWidth={"md"}>
      <DialogTitle> Delete Referral </DialogTitle>
      <DialogContent>Are you Sure you want to Delete this Referral ?</DialogContent>

      <DialogActions>
        <Button onClick={deleteReferral} style={{ color: "white", background: palette.brand[500] }}>
          Delete
        </Button>
        <Button
          onClick={CloseDeleteDialogHandler}
          style={{ color: "white", background: palette.pink[400] }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
